import * as React from 'react';

import { cn } from '../lib/utils.js';

type DimeTableProps = React.ComponentProps<'table'> & {
  /** Makes the scroll container a labelled, keyboard-focusable `region`; don't also pass `aria-label` or `tabIndex` to the table, which would add a second tab stop. */
  scrollRegionLabel?: string;
  /** Replaces the default `overflow-x-auto` on the scroll container, e.g. with `overflow-auto` to scroll both axes. */
  containerClassName?: string;
  /** Applied to the scroll container, e.g. a `max-height` to bound the body. */
  containerStyle?: React.CSSProperties;
};

export function DimeTable({
  scrollRegionLabel,
  containerClassName,
  containerStyle,
  className,
  ...props
}: Readonly<DimeTableProps>) {
  return (
    <div
      data-slot="table-container"
      role={scrollRegionLabel ? 'region' : undefined}
      aria-label={scrollRegionLabel}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- tabIndex is set only together with role="region" so keyboard users can scroll it
      tabIndex={scrollRegionLabel ? 0 : undefined}
      style={containerStyle}
      className={cn(
        'relative w-full overflow-x-auto',
        // outline-hidden, not outline-none, and only on focus, so forced colors paints its 2px outline on focus and none at rest.
        scrollRegionLabel &&
          'focus-visible:outline-hidden focus-visible:ring-[3px] focus-visible:ring-ring/50',
        containerClassName,
      )}
    >
      <table
        data-slot="table"
        className={cn('w-full caption-bottom text-sm', className)}
        {...props}
      />
    </div>
  );
}
